// ─────────────────────────────────────────────────────────────────────────────
// Archivo: useTabs.js
// Descripción: Hook personalizado para gestión de pestañas con aislamiento por APP.
// ─────────────────────────────────────────────────────────────────────────────
import React from 'react';
import { getStoreValueBo, getStoreValue } from '../gx/GxStore';

/**
 * @param {{ navigate: Function, navClear: Function, navTo: Function }} deps
 */
export const useTabs = ({ navigate, navClear, navTo, isStartupResetKey = () => false }) => {
  // Obtenemos el identificador de la app actual (vett, opti, invo, spaw)
  const strApp = getStoreValue('StoreSession', 'txApp') || 'default';

  // ─── CLAVES EXCLUIDAS (Definidas dentro para usar strApp) ──────────────────
  const setExcludedKeys = new Set([
    'StoreSession',
    'MxMain.tabs',
    'MxMain.activeTabId',
    `MxMain.pinnedTabs_${strApp}`,
    'useNav.urlStack',
    'useNav.count',
    'trans',
    'access'
  ]);

  // ─── HELPERS DE PESTAÑAS FIJADAS (aisladas por app en localStorage) ───────
  const _loadPinnedTabs = () => {
    try {
      const strKey = `MxMain.pinnedTabs_${strApp}`;
      const data = JSON.parse(localStorage.getItem(strKey) || '[]');
      const arrData = !Array.isArray(data) ? Object.values(data) : data;
      
      return arrData.map(tab => ({
        ...tab,
        urlStack: [],
        storeSnapshot: undefined
      }));
    } catch (e) {
      return [];
    }
  };

  const _savePinnedTabs = (arrPinned) => {
    const strKey = `MxMain.pinnedTabs_${strApp}`;
    const arrCleanPinned = arrPinned.map(tab => ({
      ...tab,
      urlStack: [],
      storeSnapshot: undefined
    }));
    localStorage.setItem(strKey, JSON.stringify(arrCleanPinned));
  };

  // ─── HELPERS DE PERSISTENCIA ────────────────────────────────────────────
  const _readTabs = () =>
    JSON.parse(sessionStorage.getItem('MxMain.tabs') || '[]');

  const _clearStartupState = () => {
    for (let intIndex = sessionStorage.length - 1; intIndex >= 0; intIndex--) {
      const strKey = sessionStorage.key(intIndex);
      if (strKey && isStartupResetKey(strKey)) {
        sessionStorage.removeItem(strKey);
      }
    }

    const arrSessionTabs = _readTabs();
    const arrResetTabs = arrSessionTabs.map(obTab => {
      if (!obTab.storeSnapshot) return obTab;
      const obStoreSnapshot = { ...obTab.storeSnapshot };
      Object.keys(obStoreSnapshot).forEach(strKey => {
        if (isStartupResetKey(strKey)) delete obStoreSnapshot[strKey];
      });
      return { ...obTab, storeSnapshot: obStoreSnapshot };
    });

    sessionStorage.setItem('MxMain.tabs', JSON.stringify(arrResetTabs));
    return arrResetTabs;
  };

  const _readActiveTabId = () =>
    sessionStorage.getItem('MxMain.activeTabId') || null;

  const _persistTabs = (arrNewTabs) => {
    setTabs(arrNewTabs);
    try {
      sessionStorage.setItem('MxMain.tabs', JSON.stringify(arrNewTabs));
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        console.warn("Tab memory limit reached!");
      }
    }
  };

  const _persistActiveTabId = (strId) => {
    setActiveTabId(strId);
    if (strId) sessionStorage.setItem('MxMain.activeTabId', strId);
    else sessionStorage.removeItem('MxMain.activeTabId');
  };

  // ─── HELPERS DE SNAPSHOT / RESTORE ───────────────────────────────────────
  const _snapshotCurrentTab = (strActiveId, arrCurrentTabs) => {
    const arrCurrentStack = JSON.parse(
      sessionStorage.getItem('useNav.urlStack') || '[]'
    );
    const obStoreSnapshot = {};
    for (let i = 0; i < sessionStorage.length; i++) {
      const strKey = sessionStorage.key(i);
      if (!strKey || setExcludedKeys.has(strKey)) continue;
      obStoreSnapshot[strKey] = sessionStorage.getItem(strKey);
    }
    return arrCurrentTabs.map(t =>
      t.id === strActiveId ? { ...t, urlStack: arrCurrentStack, storeSnapshot: obStoreSnapshot } : t
    );
  };

  const _restoreTabStores = (obTab) => {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const strKey = sessionStorage.key(i);
      if (!strKey || setExcludedKeys.has(strKey)) continue;
      sessionStorage.removeItem(strKey);
    }
    if (!obTab.storeSnapshot) return;
    Object.keys(obTab.storeSnapshot).forEach(strKey => {
      const value = obTab.storeSnapshot[strKey];
      if (value !== null && value !== undefined) {
        sessionStorage.setItem(strKey, value);
      }
    });
  };

  // ─── NAVEGACIÓN BASE ──────────────────────────────────────────────────────
  const _closeAllAndGoBlank = () => {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const strKey = sessionStorage.key(i);
      if (!strKey || setExcludedKeys.has(strKey)) continue;
      sessionStorage.removeItem(strKey);
    }
    gotoBlank();
  };

  const gotoBlank = () => {
    _persistTabs([]);
    _persistActiveTabId(null);
    navClear();
    navTo('blank');
  };

  // ─── ESTADO ───────────────────────────────────────────────────────────────
  const [tabs, setTabs] = React.useState(() => {
    const arrSessionTabs = _clearStartupState();
    const boShowTabs = getStoreValueBo('StoreSession', 'boShowTabs');
    const arrPinned = boShowTabs ? _loadPinnedTabs() : [];

    const arrPinnedToAdd = arrPinned.filter(pt => 
      !arrSessionTabs.some(t => t.iconKey === pt.iconKey)
    );

    const arrAllTabs = [...arrPinnedToAdd, ...arrSessionTabs];
    if (arrPinnedToAdd.length > 0) {
      sessionStorage.setItem('MxMain.tabs', JSON.stringify(arrAllTabs));
    }
    return arrAllTabs;
  });

  const [activeTabId, setActiveTabId] = React.useState(() => {
    const strStoredActiveId = sessionStorage.getItem('MxMain.activeTabId');
    const arrCurrentTabs = JSON.parse(sessionStorage.getItem('MxMain.tabs') || '[]');
    const boIsValid = arrCurrentTabs.some(t => t.id === strStoredActiveId);

    if (strStoredActiveId && boIsValid) return strStoredActiveId;
    if (arrCurrentTabs.length > 0) {
      const strFirstId = arrCurrentTabs[0].id;
      sessionStorage.setItem('MxMain.activeTabId', strFirstId);
      return strFirstId;
    }
    return null;
  });

  // ─── EFECTO DE INICIALIZACIÓN ─────────────────────────────────────────────
  React.useEffect(() => {
    const arrCurrentTabs = _readTabs();
    const strActiveId = _readActiveTabId();

    if (arrCurrentTabs.length > 0 && strActiveId) {
      const obActiveTab = arrCurrentTabs.find(t => t.id === strActiveId);
      if (obActiveTab) {
        _restoreTabStores(obActiveTab);
        if (obActiveTab.urlStack?.length > 0) {
          sessionStorage.setItem('useNav.urlStack', JSON.stringify(obActiveTab.urlStack));
          sessionStorage.setItem('useNav.count', obActiveTab.urlStack.length.toString());
          navigate(`/main/${obActiveTab.urlStack[obActiveTab.urlStack.length - 1]}`, { replace: true });
        } else {
          navClear();
          Promise.all([navTo('blank')]).then(() => navTo(obActiveTab.iconKey));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── REORDENAMIENTO ───────────────────────────────────────────────────────
  const handleReorderTabs = (strSourceId, strTargetId) => {
    const arrCurrentTabs = _readTabs();
    const intFromIdx = arrCurrentTabs.findIndex(t => t.id === strSourceId);
    const intToIdx = arrCurrentTabs.findIndex(t => t.id === strTargetId);
    if (intFromIdx === -1 || intToIdx === -1) return;

    const arrReordered = [...arrCurrentTabs];
    const [obMoved] = arrReordered.splice(intFromIdx, 1);
    arrReordered.splice(intToIdx, 0, obMoved);
    _persistTabs(arrReordered);

    const arrNewPinned = arrReordered.filter(t => t.pinned);
    if (arrNewPinned.length > 0) {
      _savePinnedTabs(arrNewPinned);
    }
  };

  // ─── ACCIONES PÚBLICAS ────────────────────────────────────────────────────
  const switchToTab = (strTabId) => {
    const arrCurrentTabs = _readTabs();
    const strCurrentActiveId = _readActiveTabId();
    if (strTabId === strCurrentActiveId) return;

    const arrUpdatedTabs = _snapshotCurrentTab(strCurrentActiveId, arrCurrentTabs);
    _persistTabs(arrUpdatedTabs);
    _persistActiveTabId(strTabId);

    const obTargetTab = arrUpdatedTabs.find(t => t.id === strTabId);
    if (obTargetTab) {
      _restoreTabStores(obTargetTab);
      if (obTargetTab.urlStack?.length > 0) {
        sessionStorage.setItem('useNav.urlStack', JSON.stringify(obTargetTab.urlStack));
        sessionStorage.setItem('useNav.count', obTargetTab.urlStack.length.toString());
        navigate(`/main/${obTargetTab.urlStack[obTargetTab.urlStack.length - 1]}`, { replace: true });
      } else {
        navClear();
        Promise.all([navTo('blank')]).then(() => navTo(obTargetTab.iconKey));
      }
    }
  };

  const closeTab = (obEvent, strTabId) => {
    if (obEvent) { obEvent.stopPropagation(); obEvent.preventDefault(); }
    const arrCurrentTabs = _readTabs();
    const obTabToClose = arrCurrentTabs.find(t => t.id === strTabId);
    if (obTabToClose && obTabToClose.pinned) return;

    const strCurrentActiveId = _readActiveTabId();
    const intIdx = arrCurrentTabs.findIndex(t => t.id === strTabId);
    const arrNewTabs = arrCurrentTabs.filter(t => t.id !== strTabId);

    if (strTabId !== strCurrentActiveId) {
      _persistTabs(arrNewTabs);
      return;
    }

    if (arrNewTabs.length === 0) {
      _closeAllAndGoBlank();
    } else {
      const obNewActiveTab = arrNewTabs[Math.min(intIdx, arrNewTabs.length - 1)];
      _persistTabs(arrNewTabs);
      _persistActiveTabId(obNewActiveTab.id);
      _restoreTabStores(obNewActiveTab);
      if (obNewActiveTab.urlStack?.length > 0) {
        sessionStorage.setItem('useNav.urlStack', JSON.stringify(obNewActiveTab.urlStack));
        sessionStorage.setItem('useNav.count', obNewActiveTab.urlStack.length.toString());
        navigate(`/main/${obNewActiveTab.urlStack[obNewActiveTab.urlStack.length - 1]}`, { replace: true });
      } else {
        navClear();
        Promise.all([navTo('blank')]).then(() => navTo(obNewActiveTab.iconKey));
      }
    }
  };

  const closeAllTabs = () => {
    const arrCurrentTabs = _readTabs();
    const arrPinnedTabs = arrCurrentTabs.filter(t => t.pinned);
    if (arrPinnedTabs.length === 0) {
      _closeAllAndGoBlank();
      return;
    }
    _persistTabs(arrPinnedTabs);
    const obFirstPinned = arrPinnedTabs[0];
    _persistActiveTabId(obFirstPinned.id);
    _restoreTabStores(obFirstPinned);
    if (obFirstPinned.urlStack?.length > 0) {
      sessionStorage.setItem('useNav.urlStack', JSON.stringify(obFirstPinned.urlStack));
      sessionStorage.setItem('useNav.count', obFirstPinned.urlStack.length.toString());
      navigate(`/main/${obFirstPinned.urlStack[obFirstPinned.urlStack.length - 1]}`, { replace: true });
    } else {
      navClear();
      Promise.all([navTo('blank')]).then(() => navTo(obFirstPinned.iconKey));
    }
  };

  const duplicateTab = (strTabId) => {
    const arrCurrentTabs = _readTabs();
    const strCurrentActiveId = _readActiveTabId();
    const arrSnapshotTabs = _snapshotCurrentTab(strCurrentActiveId, arrCurrentTabs);
    const obSourceTab = arrSnapshotTabs.find(t => t.id === strTabId);
    if (!obSourceTab) return;

    const strNewTabId = `tab_${Date.now()}`;
    const obNewTab = {
      id: strNewTabId,
      label: obSourceTab.label,
      iconKey: obSourceTab.iconKey,
      urlStack: [...(obSourceTab.urlStack || [])],
      storeSnapshot: obSourceTab.storeSnapshot ? { ...obSourceTab.storeSnapshot } : undefined,
      pinned: false,
    };

    const intSourceIdx = arrSnapshotTabs.findIndex(t => t.id === strTabId);
    const arrFinalTabs = [
      ...arrSnapshotTabs.slice(0, intSourceIdx + 1),
      obNewTab,
      ...arrSnapshotTabs.slice(intSourceIdx + 1),
    ];

    _persistTabs(arrFinalTabs);
    _persistActiveTabId(strNewTabId);
    _restoreTabStores(obNewTab);
    sessionStorage.setItem('useNav.urlStack', JSON.stringify(obNewTab.urlStack));
    sessionStorage.setItem('useNav.count', obNewTab.urlStack.length.toString());
    
    const strLastUrl = obNewTab.urlStack[obNewTab.urlStack.length - 1];
    if (strLastUrl) navigate(`/main/${strLastUrl}`, { replace: true });
    else {
      navClear();
      Promise.all([navTo('blank')]).then(() => navTo(obNewTab.iconKey));
    }
  };

  const togglePinTab = (strTabId) => {
    const arrCurrentTabs = _readTabs();
    const obTab = arrCurrentTabs.find(t => t.id === strTabId);
    if (!obTab || obTab.options === false) return;

    const arrPinned = _loadPinnedTabs();

    if (obTab.pinned) {
      const arrNewPinned = arrPinned.filter(p => p.iconKey !== obTab.iconKey);
      _savePinnedTabs(arrNewPinned);
      _persistTabs(arrCurrentTabs.map(t =>
        t.id === strTabId ? { ...t, pinned: false } : t
      ));
    } else {
      const arrSnapshotTabs = _snapshotCurrentTab(_readActiveTabId(), arrCurrentTabs);
      const obSnapTab = arrSnapshotTabs.find(t => t.id === strTabId);
      const obPinnedTab = { ...(obSnapTab || obTab), pinned: true };

      const arrNewPinned = [...arrPinned.filter(p => p.iconKey !== obTab.iconKey), obPinnedTab];
      _savePinnedTabs(arrNewPinned);
      _persistTabs(arrSnapshotTabs.map(t =>
        t.id === strTabId ? obPinnedTab : t
      ));
    }
  };

  const openTab = (strUrl, strLabel, boShowTabs) => {
    const arrCurrentTabs = _readTabs();
    const strCurrentActiveId = _readActiveTabId();

    if (boShowTabs) {
      const obExistingTab = arrCurrentTabs.find(t => t.iconKey === strUrl);
      if (obExistingTab) {
        switchToTab(obExistingTab.id);
        return true;
      }
      const arrSnapshotTabs = strCurrentActiveId ? _snapshotCurrentTab(strCurrentActiveId, arrCurrentTabs) : arrCurrentTabs;
      const strNewTabId = `tab_${Date.now()}`;
      _persistTabs([...arrSnapshotTabs, { id: strNewTabId, label: strLabel || strUrl, iconKey: strUrl, urlStack: [], pinned: false }]);
      _persistActiveTabId(strNewTabId);
    } else {
      const strNewTabId = `tab_${Date.now()}`;
      _persistTabs([{ id: strNewTabId, label: strLabel || strUrl, iconKey: strUrl, urlStack: [], pinned: false }]);
      _persistActiveTabId(strNewTabId);
    }
    return false;
  };

  const openTabWithNavigation = (strIconKey, strLabel, boShowTabs, boOptions = true, strTabInstanceKey = null) => {
    if (!boShowTabs) {
      navClear();
      navTo(strIconKey);
      return;
    }
    const arrCurrentTabs = _readTabs();
    const strCurrentActiveId = _readActiveTabId();
    const obExistingTab = arrCurrentTabs.find(t =>
      t.iconKey === strIconKey && (!strTabInstanceKey || t.instanceKey === strTabInstanceKey)
    );
    if (obExistingTab) {
      switchToTab(obExistingTab.id);
      return;
    }
    const arrSnapshotTabs = strCurrentActiveId ? _snapshotCurrentTab(strCurrentActiveId, arrCurrentTabs) : arrCurrentTabs;
    const strNewTabId = `tab_${Date.now()}`;
    const obNewTab = { id: strNewTabId, label: strLabel || strIconKey, iconKey: strIconKey, instanceKey: strTabInstanceKey, urlStack: [], pinned: false, options: boOptions };
    _persistTabs([...arrSnapshotTabs, obNewTab]);
    _persistActiveTabId(strNewTabId);
    navClear();
    Promise.all([navTo('blank')]).then(() => navTo(strIconKey));
  };

  return {
    tabs,
    activeTabId,
    gotoBlank,
    openTab,
    openTabWithNavigation,
    handleReorderTabs,
    switchToTab,
    closeTab,
    closeAllTabs,
    duplicateTab,
    togglePinTab,
  };
}